import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { X } from "lucide-react";

/**
 * Simple drill-down list of assets in a given category, opened from the
 * "Assets by category" pie legend on Reports. Read-only — no edit fields.
 */
const AssetCategoryDrillDialog = ({ open, category, count, onClose }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !category) return;
    setLoading(true);
    api
      .get("/assets", { params: { category } })
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [open, category]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      data-testid="asset-cat-drill-dialog"
      onClick={onClose}
    >
      <div
        className="nb-card w-[min(640px,94vw)] max-h-[88vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-white px-5 py-4 border-b-2 border-black flex items-center justify-between">
          <div>
            <div className="label-eyebrow">Reports · Assets by category</div>
            <h3 className="font-display text-2xl">{category}</h3>
            <div className="text-xs text-[#607087] mt-0.5">
              {count} item{count === 1 ? "" : "s"} · read-only
            </div>
          </div>
          <button
            data-testid="asset-cat-drill-close"
            onClick={onClose}
            className="nb-btn bg-white p-2"
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        <div className="p-5">
          {loading && (
            <div className="text-sm text-[#607087] py-4 text-center">Loading…</div>
          )}
          {!loading && rows.length === 0 && (
            <div className="text-sm text-[#607087] py-4 text-center italic">
              No assets found in this category.
            </div>
          )}
          {!loading && rows.length > 0 && (
            <div className="flex flex-col gap-2">
              {rows.map((a) => (
                <div
                  key={a.id}
                  data-testid={`asset-drill-row-${a.id}`}
                  className="nb-card-sm p-3 flex items-center gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-display truncate">{a.name}</div>
                    <div className="text-xs text-[#607087] flex flex-wrap gap-x-2">
                      {a.serial && <span>SN: {a.serial}</span>}
                      {a.department && <span>· {a.department}</span>}
                      {a.assignee && <span>· held by {a.assignee}</span>}
                    </div>
                  </div>
                  <span
                    className="text-[11px] px-2 py-0.5 rounded border-2 border-black font-semibold"
                    style={{
                      background:
                        a.status === "broken"
                          ? "#FEE2E2"
                          : a.status === "in_use"
                            ? "#DBEAFE"
                            : a.status === "retired"
                              ? "#E5E7EB"
                              : "#DCFCE7",
                    }}
                  >
                    {a.status || "—"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssetCategoryDrillDialog;
